import { CloseCircleFilled, SearchUniversal, SendUniversal, TickMarkFilled, TrashCanOutlined, UploadUniversal } from '@duxton-web/icons';
import { Button, Flex, Input, Modal, Select, Table, Tag, Typography, Upload, theme } from '@duxton-web/ui';
import type { UploadFile } from '@duxton-web/ui';
import React, { useEffect, useRef, useState } from 'react';
import type { JourneyTestNotificationRequest } from 'omega-rtc/actions/journey-test-notification';
import { JOURNEY_TEST_NOTIFICATION_CSV_LIMITS } from 'omega-rtc/config';
import { getCountryFlag } from 'omega-rtc/utils/country-flag';
import { getHedwigTemplateUrl } from 'omega-rtc/utils/hedwig-template-url';
import { getJourneyTestRecipient, isSingleJourneyRecipient } from 'omega-rtc/utils/journey-notification-recipient';
import {
  decodeJourneyCsv, JourneyCsvLimits, JourneyCsvRecord, JourneyCsvResult, journeyCsvRequestKey,
  JOURNEY_NOTIFICATION_SAMPLE_CSV, parseJourneyNotificationCsv, resolveJourneyCsvRecords, validateJourneyCsvFile,
} from 'omega-rtc/utils/journey-notification-csv';
import type { JourneyGroupInput } from 'omega-rtc/utils/journey-template-matcher';
import { formatJourneyAttributeValue } from './JourneyTemplateVariantMapping';

type Props = {
  campaignKey: string; countryName: string; countryCode?: string; countryID?: number; audience?: string;
  groups: JourneyGroupInput[]; isSynced: boolean; isSyncInProgress: boolean; isPending?: boolean;
  limits?: JourneyCsvLimits;
  // eslint-disable-next-line no-unused-vars -- Function-type parameters.
  onSend: (request: JourneyTestNotificationRequest) => Promise<unknown>;
  // eslint-disable-next-line no-unused-vars -- Function-type parameters.
  onBusyChange: (busy: boolean) => void;
};
type Outcome = { status: 'sending' | 'succeeded' | 'failed' | 'duplicate' | 'not-sent'; message?: string };
type Snapshot = { context: string; mappings: string; rows: JourneyCsvResult[] };
type Candidate = JourneyCsvResult['candidates'][number];

const outcomeText: Record<Outcome['status'], string> = {
  sending: 'Sending…',
  succeeded: 'Request succeeded',
  failed: 'Request failed — verify delivery',
  duplicate: 'Duplicate skipped',
  'not-sent': 'Not sent',
};

export const JourneyCsvTestNotification = ({ limits = JOURNEY_TEST_NOTIFICATION_CSV_LIMITS, ...props }: Props) => {
  const { token } = theme.useToken();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [records, setRecords] = useState<JourneyCsvRecord[]>();
  const [reading, setReading] = useState(false);
  const [snapshot, setSnapshot] = useState<Snapshot>();
  const [choices, setChoices] = useState<Record<number, number>>({});
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [recipientID, setRecipientID] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const [summary, setSummary] = useState('');
  const mounted = useRef(true);
  const reader = useRef<FileReader>();
  const readGeneration = useRef(0);
  const inFlight = useRef(false);
  const recipient = getJourneyTestRecipient(props.audience);
  const mappingsKey = JSON.stringify(props.groups);
  const identity = JSON.stringify([props.campaignKey, props.countryID, props.audience]);
  const context = JSON.stringify([identity, props.countryCode, mappingsKey, props.isSynced, props.isSyncInProgress]);
  const ready = Boolean(props.campaignKey && props.isSynced && !props.isSyncInProgress
    && props.groups.some(group => group && Array.isArray(group.variants) && group.variants.length));
  const current = useRef({ ...props, identity, context, ready });
  current.current = { ...props, identity, context, ready };
  const busy = sending || Boolean(props.isPending);
  const rows = snapshot && snapshot.context === context && snapshot.mappings === mappingsKey ? snapshot.rows : [];
  const countryCode = typeof props.countryCode === 'string' && /^[a-z]{2}$/i.test(props.countryCode.trim())
    ? props.countryCode.trim().toUpperCase() : '';
  const id = recipientID.trim();
  const requestFor = (row: JourneyCsvResult, candidate: Candidate): JourneyTestNotificationRequest => ({
    recipientID: id,
    recipientType: recipient!.type,
    template: { id: candidate.templateID, enableLangDetection: true, extraParams: row.attributes },
  });
  const keyFor = (row: JourneyCsvResult, candidate?: Candidate) => (
    candidate && recipient ? journeyCsvRequestKey(requestFor(row, candidate)) : ''
  );
  const selected = rows.filter(row => selectedRows.includes(String(row.row)) && row.candidates[choices[row.row]]);
  const distinct = new Set(selected.map(row => keyFor(row, row.candidates[choices[row.row]])));

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; readGeneration.current += 1; reader.current?.abort(); };
  }, []);
  useEffect(() => {
    setSnapshot(undefined); setChoices({}); setSelectedRows([]); setConfirming(false); setSummary(''); setError('');
  }, [context]);
  useEffect(() => {
    readGeneration.current += 1; reader.current?.abort();
    setFiles([]); setRecords(undefined); setReading(false); setRecipientID(''); setOutcomes({});
    if (!inFlight.current) props.onBusyChange(false);
  }, [identity, props.onBusyChange]);

  const clearResults = () => {
    setSnapshot(undefined); setChoices({}); setSelectedRows([]); setConfirming(false); setSummary(''); setError('');
  };
  const removeFile = () => {
    if (inFlight.current || busy) return false;
    readGeneration.current += 1; reader.current?.abort(); setFiles([]); setRecords(undefined); setReading(false);
    clearResults(); props.onBusyChange(false); return true;
  };
  const publishMatches = (input: JourneyCsvRecord[]) => {
    const latest = current.current;
    if (!latest.ready) return;
    const resolved = resolveJourneyCsvRecords(input, latest.groups, latest.countryID);
    const defaults: Record<number, number> = {};
    resolved.forEach(row => { if (row.candidates.length === 1) defaults[row.row] = 0; });
    setChoices(defaults);
    setSelectedRows(resolved.filter(row => row.candidates.length === 1).map(row => String(row.row)));
    setSnapshot({ context: latest.context, mappings: JSON.stringify(latest.groups), rows: resolved });
    setError(''); setSummary('');
  };
  const loadFile = (file: File & { uid?: string }) => {
    if (busy || inFlight.current) return false;
    readGeneration.current += 1; const generation = readGeneration.current; reader.current?.abort(); clearResults(); setRecords(undefined);
    try { validateJourneyCsvFile(file, limits); } catch (failure) { setFiles([]); setReading(false); props.onBusyChange(false); setError((failure as Error).message); return false; }
    setFiles([{ uid: file.uid || String(generation), name: file.name, size: file.size, status: 'done' }]);
    setReading(true); props.onBusyChange(true);
    const fileReader = new FileReader(); reader.current = fileReader;
    const finish = () => {
      if (mounted.current && generation === readGeneration.current) { setReading(false); props.onBusyChange(false); }
    };
    fileReader.onload = () => {
      if (!mounted.current || generation !== readGeneration.current || current.current.identity !== identity) return;
      try {
        const parsed = parseJourneyNotificationCsv(decodeJourneyCsv(fileReader.result as ArrayBuffer), limits);
        setRecords(parsed); publishMatches(parsed);
      } catch (failure) { setError((failure as Error).message); }
      finish();
    };
    fileReader.onerror = () => {
      if (mounted.current && generation === readGeneration.current) setError('Unable to read the CSV file.');
      finish();
    };
    fileReader.onabort = finish;
    fileReader.readAsArrayBuffer(file);
    return false; // Local reading only. Never invoke Upload's network transport.
  };
  const findMatches = () => {
    if (!records || reading || busy || inFlight.current || !ready) return;
    publishMatches(records);
  };
  const validateContext = () => snapshot && mounted.current && current.current.ready
    && current.current.context === snapshot.context && JSON.stringify(current.current.groups) === snapshot.mappings;
  const beginConfirmation = () => {
    if (busy || reading || inFlight.current || !selected.length || !recipient) return;
    if (!isSingleJourneyRecipient(recipientID)) { setError(`Enter one ${recipient.label}; recipient lists are not supported.`); return; }
    if (!validateContext()) { clearResults(); setError('Campaign data changed. Find matched templates again.'); return; }
    setError(''); setConfirming(true);
  };
  const sendSelected = async () => {
    if (inFlight.current || busy || !recipient || !isSingleJourneyRecipient(recipientID) || !selected.length) return;
    if (!validateContext()) { setConfirming(false); setError('Campaign data changed. Find matched templates again.'); return; }
    inFlight.current = true; setConfirming(false); setSending(true); props.onBusyChange(true); setError('');
    let succeeded = 0; let failed = 0; let duplicate = 0; let stopped = false; let stopMessage = '';
    const sentContext = context;
    // Deduplicate only this confirmed batch; intentional later sends remain available.
    const batchRequests = new Set<string>();
    const update = (row: JourneyCsvResult, key: string, outcome: Outcome) => {
      if (mounted.current && current.current.context === sentContext) setOutcomes(old => ({ ...old, [`${row.row}:${key}`]: outcome }));
    };
    try {
      for (const row of selected) {
        const candidate = row.candidates[choices[row.row]];
        const request = requestFor(row, candidate); const key = journeyCsvRequestKey(request);
        if (!validateContext()) { stopped = true; break; }
        if (stopped) {
          update(row, key, { status: 'not-sent', message: 'Batch stopped. No request was made for this record.' });
        } else if (batchRequests.has(key)) {
          duplicate += 1; update(row, key, { status: 'duplicate' });
        } else {
          const fresh = resolveJourneyCsvRecords([{ row: row.row, attributes: row.attributes, groupID: row.groupID }], current.current.groups, props.countryID)[0];
          if (!fresh.candidates.some(match => match.groupID === candidate.groupID && match.variantName === candidate.variantName
          && match.templateID === candidate.templateID && match.channel === candidate.channel)) {
            stopped = true; update(row, key, { status: 'not-sent', message: 'Template mappings changed. Rematch before sending.' });
          } else {
            batchRequests.add(key); update(row, key, { status: 'sending' });
            try {
              // Keep concurrency at one; do not retry or burst requests at the preview service.
              // eslint-disable-next-line no-await-in-loop
              await props.onSend(request); succeeded += 1; update(row, key, { status: 'succeeded' });
            } catch (failure) {
              failed += 1; update(row, key, { status: 'failed' });
              const status = (failure as { response?: { status?: number } })?.response?.status;
              if (!status || status === 401 || status === 403 || status === 429 || status >= 500) {
                stopped = true;
                if (status === 401 || status === 403) stopMessage = 'Authorization failed; remaining records were not sent.';
                else if (status === 429) stopMessage = 'Preview requests were rate limited; remaining records were not sent.';
                else stopMessage = 'Preview service unavailable or outcome uncertain; remaining records were not sent.';
              }
            }
          }
        }
      }
      if (mounted.current && current.current.context === sentContext) {
        setSummary(`${succeeded} succeeded, ${failed} failed, ${duplicate} duplicates skipped.${stopped ? ' Batch stopped; verify delivery before trying any remaining records.' : ''}`);
        setSelectedRows([]);
        if (stopMessage) setError(stopMessage);
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) { setSending(false); props.onBusyChange(false); }
    }
  };
  const downloadSample = () => {
    const url = URL.createObjectURL(new Blob([JOURNEY_NOTIFICATION_SAMPLE_CSV], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a'); link.href = url; link.download = 'journey-test-notification-sample.csv';
    document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
  };
  const data = rows.map(row => {
    const candidate = row.candidates[choices[row.row]];
    const key = keyFor(row, candidate);
    return { ...row,
      key: String(row.row),
      candidate,
      error: candidate ? undefined : row.error,
      outcome: outcomes[`${row.row}:${key}`] };
  });
  const resultCell = (row: typeof data[number]) => (
    <Flex vertical gap="6px">
      <Flex align="center" gap="6px">
        {row.error ? <CloseCircleFilled aria-label="Template match failed" style={{ color: token.colorError, flexShrink: 0 }} />
          : <TickMarkFilled aria-label="Template matched" style={{ color: token.colorSuccess, flexShrink: 0 }} />}
        {row.error ? <Typography.Text type="danger">{row.error}</Typography.Text>
          : <a href={getHedwigTemplateUrl(row.candidate!.templateID, row.candidate!.channel)} target="_blank" rel="noreferrer">{row.candidate!.templateID}</a>}
      </Flex>
      {row.candidates.length > 1 && <Select
        virtual={false} aria-label={`Matched template for CSV row ${row.row}`} placeholder="Select a matched variant"
        value={choices[row.row] === undefined ? undefined : String(choices[row.row])} disabled={busy}
        options={row.candidates.map((match, index) => ({ value: String(index), label: `${match.groupID} / ${match.variantName || match.templateID}` }))}
        onChange={(value) => {
          if (inFlight.current || busy) return;
          const index = Number(value); setChoices(old => ({ ...old, [row.row]: index }));
          setSelectedRows(old => Array.from(new Set([...old, row.key])));
        }}
      />}
    </Flex>
  );

  return (
    <Flex vertical gap="16px">
      <div data-dd-privacy="hidden">
        <Upload
          aria-label="CSV attributes file" accept=".csv,text/csv" maxCount={1} fileList={files} disabled={busy}
          beforeUpload={loadFile} onRemove={removeFile} showUploadList={{ showPreviewIcon: false, showRemoveIcon: !busy, removeIcon: <TrashCanOutlined aria-label="Remove CSV file" /> }}
        >
          <Button icon={<UploadUniversal aria-hidden="true" />} disabled={busy}>Upload CSV</Button>
        </Upload>
      </div>
      <Typography.Text type="secondary">
        Use attribute columns (text values; blank cells are missing), or a userAttributes JSON column for typed values.
        Optional groupID scopes a record. UTF-8 only; maximum {Math.floor(limits.maxBytes / 1024)} KiB and {limits.maxRows} records.
      </Typography.Text>
      <div><Button type="link" disabled={busy} onClick={downloadSample}>Download sample CSV</Button></div>
      {reading && <Typography.Text role="status">Reading CSV…</Typography.Text>}
      {records && <Typography.Text role="status">Loaded {records.length} CSV records. Matching does not send notifications.</Typography.Text>}
      {!ready && <Typography.Text type="secondary">Synchronized campaign template data is required.</Typography.Text>}
      {!recipient && <Typography.Text type="danger">Unsupported campaign audience. Test notification is unavailable.</Typography.Text>}
      {error && <Typography.Text type="danger" role="alert">{error}</Typography.Text>}
      <div><Button
        icon={<SearchUniversal aria-hidden="true" />} disabled={!ready || busy || reading || !records}
        onClick={findMatches}
      >Find Matched Template</Button></div>
      {!!rows.length && <React.Fragment>
        <Typography.Title level={4} style={{ margin: '8px 0 0' }}>Mapped template IDs</Typography.Title>
        <Table
          className="journey-test-notification-table" dataSource={data} rowKey="key" size="small"
          pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
          rowSelection={{ selectedRowKeys: selectedRows,
            getCheckboxProps: row => ({ disabled: busy || !row.candidate, 'aria-label': `Send CSV row ${row.row}` }),
            onChange: keys => { if (!busy && !inFlight.current) setSelectedRows(keys.map(String)); } }}
          columns={[
            { key: 'row', dataIndex: 'row', title: 'Row', width: 64 },
            { key: 'country',
              title: 'Country',
              render: () => (<Flex align="center" gap="6px" wrap="nowrap">
                <span role="img" aria-label={countryCode ? `${props.countryName} flag` : 'Country flag unavailable'}>{getCountryFlag(countryCode)}</span>
                <span>{countryCode || props.countryName}</span>
              </Flex>) },
            { key: 'attributes',
              dataIndex: 'attributes',
              title: 'User attributes',
              render: attributes => (<Flex gap="6px" wrap="wrap" data-dd-privacy="hidden">
                {Object.entries(attributes).map(([key, value]) => <Tag key={key}>{key}: {formatJourneyAttributeValue(value)}</Tag>)}
              </Flex>) },
            { key: 'group',
              title: 'Group ID',
              onCell: row => ({ colSpan: row.error ? 3 : 1 }),
              render: (_, row) => (row.error ? resultCell(row) : row.candidate!.groupID) },
            { key: 'variant', title: 'Variant name', onCell: row => ({ colSpan: row.error ? 0 : 1 }), render: (_, row) => row.candidate?.variantName || '—' },
            { key: 'template', title: 'Mapped template ID', onCell: row => ({ colSpan: row.error ? 0 : 1 }), render: (_, row) => resultCell(row) },
            { key: 'outcome',
              title: 'Test result',
              render: (_, row) => (<Typography.Text type={row.outcome?.status === 'failed' ? 'danger' : undefined}>
                {row.outcome ? outcomeText[row.outcome.status] : 'Not sent'}
              </Typography.Text>) },
          ]}
        />
        <Typography.Text type="secondary">
          {selected.length} CSV records selected ({distinct.size} distinct notifications). Identical requests are sent once per confirmed batch.
          All requests use the test recipient entered below, never recipient IDs from the CSV.
        </Typography.Text>
        <Flex align="end" gap="12px" wrap="wrap">
          <Flex vertical gap="6px" style={{ flex: '1 1 320px' }}>
            <Typography.Text strong>{recipient?.label || 'Recipient ID'}</Typography.Text>
            <Input
              aria-label={recipient?.label || 'Recipient ID'} placeholder={`Enter ${recipient?.label || 'recipient ID'}`}
              data-dd-privacy="hidden" value={recipientID} disabled={busy || !recipient}
              onPaste={event => { if (/[,\r\n]/.test(event.clipboardData.getData('text'))) { event.preventDefault(); setError('Enter one test recipient ID; recipient lists are not supported.'); } }}
              onChange={event => { if (busy || inFlight.current) return; setRecipientID(event.target.value); setError(''); setSummary(''); }}
            />
          </Flex>
          <Button
            type="primary" icon={<SendUniversal aria-hidden="true" />} loading={busy}
            disabled={!ready || busy || reading || !recipient || !id || !selected.length} onClick={beginConfirmation}
          >Test notification</Button>
        </Flex>
      </React.Fragment>}
      {sending && <Typography.Text role="status">Sending CSV test notifications…</Typography.Text>}
      {summary && <Typography.Text role="status">{summary}</Typography.Text>}
      <Modal
        title="Confirm CSV test notifications" open={confirming} okText="Send test notifications"
        onOk={sendSelected} onCancel={() => setConfirming(false)} destroyOnClose
      >
        <Typography.Paragraph>
          This sends {distinct.size} distinct test notification requests for {selected.length} selected CSV records.
          Previously sent records can be selected and sent again. Requests are never automatically retried.
        </Typography.Paragraph>
        <Typography.Paragraph data-dd-privacy="hidden">Test recipient: {recipient?.label} {id}</Typography.Paragraph>
      </Modal>
    </Flex>
  );
};
